/**
 * Member lookup by Member QR or phone (Req 14.7), then the actions for that member:
 * on-behalf code scanning, Invoice_Earn and Point_Discount (Req 28.2).
 */
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { formatPercentBps, formatPoints } from '@macksons/i18n';
import { asApiError, callOperation } from '@macksons/web-client';
import { ClaimPanel } from '../components/ClaimPanel';
import { DiscountPanel } from '../components/DiscountPanel';
import { InvoicePanel } from '../components/InvoicePanel';
import { QrScanner } from '../components/QrScanner';
import { FeedbackRegion, OnlineOnlyNotice, useErrorText, useOnline, type Feedback } from '../components/ui';
import { useLocale, useT } from '../hooks';
import { classifyScan, isMemberQr, isPhone } from '../scanner/inputs';
import { scanMismatchText } from '../scanner/messages';
import { useServices } from '../services';
import type { MemberLookupResult } from '../types';

function MemberLookup({ onFound }: { onFound: (member: MemberLookupResult) => void }) {
  const t = useT();
  const errorText = useErrorText();
  const { client, config } = useServices();
  const online = useOnline();
  const inputId = useId();
  const hintId = useId();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>();
  const inputRef = useRef<HTMLInputElement>(null);
  const lookup = async (query: { phone: string } | { qr: string }) => {
    setBusy(true);
    setFeedback({ tone: 'info', text: t('operator.lookup.searching') });
    try {
      const member = await callOperation<MemberLookupResult>(client, 'lookupMember', { query });
      setFeedback(undefined);
      onFound(member);
    } catch (cause) {
      const err = asApiError(cause);
      setFeedback({ tone: 'error', text: err.code === 'not_found' ? t('operator.lookup.notFound') : errorText(err) });
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = value.trim();
    // A keyboard-wedge scanner types the Member QR into the field.
    if (isMemberQr(text)) void lookup({ qr: text });
    else if (isPhone(text)) void lookup({ phone: text });
    else {
      setFeedback({ tone: 'error', text: t('operator.lookup.invalidInput') });
      inputRef.current?.focus();
    }
  };
  const onScan = (text: string) => {
    const scan = classifyScan(text, config.qrHost);
    if (scan.kind === 'member_qr') void lookup({ qr: scan.token });
    else setFeedback({ tone: 'error', text: scanMismatchText(t, scan, 'member') });
  };
  return (
    <section aria-labelledby={`${inputId}-title`} className="panel">
      <h2 id={`${inputId}-title`}>{t('operator.lookup.title')}</h2>
      {!online && <OnlineOnlyNotice />}

      {/* Primary action: scan the member's QR with the camera (large, full width). */}
      <QrScanner onScan={onScan} primary startLabel={t('operator.lookup.scanQr')} disabled={!online || busy} />

      {/* Secondary: type the mobile number (or a keyboard-wedge Member QR). */}
      <form onSubmit={submit} noValidate className="form">
        <div className="field">
          <label htmlFor={inputId}>{t('operator.lookup.input')}</label>
          <p id={hintId} className="hint">
            {t('operator.lookup.hint')}
          </p>
          <input
            ref={inputRef}
            id={inputId}
            className="input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            aria-describedby={hintId}
            autoComplete="off"
            inputMode="tel"
            disabled={!online}
          />
        </div>
        <button type="submit" className="button button--block" disabled={!online || busy}>
          {t('operator.lookup.submit')}
        </button>
        {!online && (
          <p className="hint" data-testid="lookup-disabled-hint">
            {t('operator.offline.needsConnection')}
          </p>
        )}
      </form>
      <FeedbackRegion feedback={feedback} testId="lookup" />
    </section>
  );
}

function MemberCard({ member, onClear }: { member: MemberLookupResult; onClear: () => void }) {
  const t = useT();
  const locale = useLocale();
  const headingRef = useRef<HTMLHeadingElement>(null);
  // Focus moves to the member after a lookup so screen readers announce who was found.
  useEffect(() => headingRef.current?.focus(), [member.memberId]);
  const tier = member.tier.tierName ?? t('operator.member.noTier');
  return (
    <section className="panel member" aria-labelledby="member-heading">
      <div className="member__head">
        <div className="member__id">
          <h2 id="member-heading" ref={headingRef} tabIndex={-1}>
            {t('operator.member.heading', { phone: member.phoneMasked })}
          </h2>
          <span className="tier-badge">{t('operator.member.tierValue', { tier, pct: formatPercentBps(member.tier.discountBps, locale) })}</span>
        </div>
        <button type="button" className="button" onClick={onClear}>
          {t('operator.member.change')}
        </button>
      </div>
      <dl className="facts">
        <div>
          <dt>{t('operator.member.type')}</dt>
          <dd>{t(`memberTypes.${member.effectiveMemberType}`)}</dd>
        </div>
        <div>
          <dt>{t('operator.member.available')}</dt>
          <dd className="points" data-testid="member-available">
            {formatPoints(BigInt(member.available), locale)}
          </dd>
        </div>
        {member.verifiedBy && (
          <div>
            <dt>{t('operator.member.verifiedBy')}</dt>
            <dd>{t(`operator.member.verified.${member.verifiedBy}`)}</dd>
          </div>
        )}
      </dl>
    </section>
  );
}

const ACTIONS = ['claim', 'invoice', 'discount'] as const;
type Action = (typeof ACTIONS)[number];

/** Segmented control (ARIA tabs: arrow keys, Home/End) for the member's actions. */
function MemberActions({ member, outletId, onAvailable }: { member: MemberLookupResult; outletId: string; onAvailable: (available: string) => void }) {
  const t = useT();
  const id = useId();
  const [active, setActive] = useState<Action>('claim');
  const tabRefs = useRef<Partial<Record<Action, HTMLButtonElement | null>>>({});
  const onKeyDown = (e: KeyboardEvent) => {
    const i = ACTIONS.indexOf(active);
    const next =
      e.key === 'ArrowRight' ? ACTIONS[(i + 1) % ACTIONS.length] : e.key === 'ArrowLeft' ? ACTIONS[(i + ACTIONS.length - 1) % ACTIONS.length] : e.key === 'Home' ? ACTIONS[0] : e.key === 'End' ? ACTIONS[ACTIONS.length - 1] : undefined;
    if (!next) return;
    e.preventDefault();
    setActive(next);
    tabRefs.current[next]?.focus();
  };
  return (
    <section className="panel">
      <div role="tablist" aria-label={t('operator.actions.label')} className="segmented">
        {ACTIONS.map((a) => (
          <button
            key={a}
            ref={(el) => {
              tabRefs.current[a] = el;
            }}
            type="button"
            role="tab"
            id={`${id}-tab-${a}`}
            aria-selected={active === a}
            aria-controls={`${id}-panel-${a}`}
            // Full label stays the accessible name and tooltip; the visible text is a short
            // label that fits a 360px segment without truncation.
            aria-label={t(`operator.actions.${a}`)}
            title={t(`operator.actions.${a}`)}
            tabIndex={active === a ? 0 : -1}
            className="segmented__tab"
            onClick={() => setActive(a)}
            onKeyDown={onKeyDown}
          >
            {t(`operator.actions.${a}Short`)}
          </button>
        ))}
      </div>
      {/* All panels stay mounted (hidden when inactive): switching tabs keeps typed input
          and an active discount Hold with its countdown. */}
      {ACTIONS.map((a) => (
        <div key={a} role="tabpanel" id={`${id}-panel-${a}`} aria-labelledby={`${id}-tab-${a}`} hidden={active !== a} className="tabs__panel">
          {a === 'claim' && <ClaimPanel member={member} active={active === a} onAvailable={onAvailable} />}
          {a === 'invoice' && <InvoicePanel member={member} outletId={outletId} />}
          {a === 'discount' && <DiscountPanel member={member} outletId={outletId} active={active === a} />}
        </div>
      ))}
    </section>
  );
}

export function CounterPage({ outletId }: { outletId: string }) {
  const t = useT();
  const [member, setMember] = useState<MemberLookupResult | undefined>();
  return (
    <>
      <h1>{t('operator.home.title')}</h1>
      {member ? (
        <>
          <MemberCard member={member} onClear={() => setMember(undefined)} />
          <MemberActions
            key={member.memberId}
            member={member}
            outletId={outletId}
            onAvailable={(available) => setMember((m) => (m ? { ...m, available } : m))}
          />
        </>
      ) : (
        <>
          <p>{t('operator.home.intro')}</p>
          <MemberLookup onFound={setMember} />
        </>
      )}
    </>
  );
}
